import type { BackupPayload } from '../format'
import { payloadMigrationV2 } from './v2'
import { payloadMigrationV3 } from './v3'
import { payloadMigrationV4 } from './v4'

/**
 * Backup payload migrations (D-031).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS AT ALL
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  An older backup must NOT be written into the database in the hope that the
 *  normal IndexedDB version-upgrade chain will fix it. The live database is
 *  already at the current version, so `upgradeneeded` never fires and the old
 *  records would simply sit there in their old shape — silently wrong.
 *
 *  Instead the payload is brought forward HERE, in memory, before anything is
 *  written, and the migrated payload is then validated in full against the
 *  current schema.
 *
 *  Each migration is a PURE function over parsed objects: no IndexedDB, no I/O,
 *  no clock. That is what makes it testable against the same fixtures as its
 *  database counterpart.
 */

export interface PayloadMigration {
  /** The schema version this migration produces. */
  readonly to: number
  readonly describe: string
  migrate(payload: BackupPayload): BackupPayload
}

/**
 * Ordered registry.
 *
 * Version 1 is the initial schema, so there is nothing to bring forward from
 * and no entry for it. Version 2 is the first real one.
 *
 * **Adding a database migration REQUIRES adding the matching entry here.** A
 * merge-blocking test asserts the two registries agree — see
 * `payloadMigrations.test.ts`.
 */
export const PAYLOAD_MIGRATIONS: readonly PayloadMigration[] = [
  payloadMigrationV2,
  payloadMigrationV3,
  payloadMigrationV4,
]

export type MigratePayloadResult =
  | { readonly ok: true; readonly payload: BackupPayload }
  | {
      readonly ok: false
      readonly reason: 'newerThanSupported' | 'noPathToCurrent'
      readonly message: string
    }

/**
 * Bring a payload forward from its recorded version to the target version.
 *
 * A payload from a NEWER version is refused outright — never partially
 * imported, and never downgraded by guessing which fields to drop.
 */
export function migratePayload(
  payload: BackupPayload,
  targetVersion: number,
  migrations: readonly PayloadMigration[] = PAYLOAD_MIGRATIONS,
): MigratePayloadResult {
  const from = payload.dbSchemaVersion

  if (from > targetVersion) {
    return {
      ok: false,
      reason: 'newerThanSupported',
      message: `This backup was created by a newer version of Project Sola (data version ${from}; this build understands ${targetVersion}). Update the app, then restore it.`,
    }
  }

  if (from === targetVersion) return { ok: true, payload }

  const applicable = migrations
    .filter((migration) => migration.to > from && migration.to <= targetVersion)
    .sort((left, right) => left.to - right.to)

  // Every intermediate version must be covered, or the result would be a
  // half-migrated payload that merely looks current.
  const covered = new Set(applicable.map((migration) => migration.to))
  for (let version = from + 1; version <= targetVersion; version += 1) {
    if (!covered.has(version)) {
      return {
        ok: false,
        reason: 'noPathToCurrent',
        message: `Project Sola cannot bring this backup forward from data version ${from} to ${targetVersion}. A step is missing.`,
      }
    }
  }

  let migrated = payload
  for (const migration of applicable) {
    migrated = migration.migrate(migrated)
  }

  return { ok: true, payload: { ...migrated, dbSchemaVersion: targetVersion } }
}
